import { Fragment, lazy, StrictMode, Suspense } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createRoot } from 'react-dom/client';
import { ApiAppParams } from 'typings/apiApp';

import { APP_ID } from './constants';

const componentMapper = { app: lazy(() => import('@/modules/app/App.container')) };

const elementIdMapper = { app: APP_ID };

type ConfigType = ApiAppParams;

export function initExternalModule(moduleName: keyof typeof componentMapper) {
  function renderModule(config: ConfigType) {
    let moduleContainer: HTMLElement | ShadowRoot | null = document.getElementById(elementIdMapper[moduleName]);

    if (moduleContainer) {
      return;
    }

    const Module = componentMapper[moduleName];

    moduleContainer = document.createElement(elementIdMapper[moduleName]);
    moduleContainer.id = elementIdMapper[moduleName];

    const wrapper = document.getElementById('saywhatt-outlet');

    if (config.isEmbedded && wrapper) {
      wrapper.appendChild(moduleContainer);
    } else {
      document.body.appendChild(moduleContainer);
    }

    if (moduleName === 'app') {
      const shadow = moduleContainer.attachShadow({ mode: 'open' });
      moduleContainer = moduleContainer.shadowRoot;

      const cache = createCache({ container: shadow, key: 'css', prepend: true });

      const root = createRoot(moduleContainer!);

      const Wrapper = process.env.REACT_APP_ENVIRONMENT === 'development' ? Fragment : StrictMode;

      return root.render(
        <Wrapper>
          <CacheProvider value={cache}>
            <Suspense fallback={<Fragment />}>
              <Module config={config} />
            </Suspense>
          </CacheProvider>
        </Wrapper>
      );
    }

    moduleContainer = document.createElement('div');
    moduleContainer.id = elementIdMapper[moduleName];
    document.body.appendChild(moduleContainer);

    if (moduleContainer && Module) {
      const root = createRoot(moduleContainer);
      root.render(
        <Suspense fallback={<Fragment />}>
          <Module config={config} />
        </Suspense>
      );
    }
  }

  return renderModule;
}
